import React, { useEffect, useRef, useState } from "react";
import { safeFormat, toWholeNumber } from "../../../../utils/financialHelper";
import * as d3 from "d3";
import "./market.less";
import { MarketPositioning, Reit } from "../../../../types/reit.types";

interface ProcessedDataPoint {
  date: Date;
  nse?: number;
  bse?: number;
}

interface ProductRow {
  metric: string;
  nse: string | number;
  bse: string | number;
}

interface MarketProps {
  reit: Reit | null;
}

const Market: React.FC<MarketProps> = ({ reit }) => {
  const chartRef = useRef<SVGSVGElement | null>(null);
  const [activeTab, setActiveTab] = useState<"NSE" | "BSE">("NSE");
  const [processedData, setProcessedData] = useState<ProcessedDataPoint[]>([]);
  const [productData, setProductData] = useState<ProductRow[]>([]);
  const zoomBehavior = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(
    null
  );
  const [activeRange, setActiveRange] = useState<number | null>(null);

  // Process data
  useEffect(() => {
    if (!reit?.historicalDataSets?.length) {
      setProcessedData([]);
      return;
    }

    const historicalDataSets = reit.historicalDataSets[0];
    if (!historicalDataSets?.marketPrice?.length) {
      setProcessedData([]);
      return;
    }

    const dataMap = new Map<string, ProcessedDataPoint>();

    historicalDataSets.marketPrice.forEach((exchange) => {
      const exchangeName = exchange.exchangeName.toLowerCase();

      exchange.historicalData.forEach((dataPoint) => {
        if (!dataPoint.date || dataPoint.value === undefined) return;

        const dateKey = dataPoint.date;
        const existingData = dataMap.get(dateKey) || {
          date: new Date(dateKey),
        };

        if (exchangeName === "nse") {
          existingData.nse = dataPoint.value;
        } else if (exchangeName === "bse") {
          existingData.bse = dataPoint.value;
        }

        dataMap.set(dateKey, existingData);
      });
    });

    // Show all data points, sorted by date
    const processedArray = Array.from(dataMap.values())
      .filter((d) => d.nse !== undefined || d.bse !== undefined)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    setProcessedData(processedArray);
  }, [reit]);

  // Process product data
  useEffect(() => {
    const newProductData: ProductRow[] = [];

    const latestYear = reit?.years?.[reit.years.length - 1];
    if (latestYear?.marketPositioning?.length) {
      const keys: (keyof MarketPositioning)[] = [
        "openingPrice",
        "closingPrice",
        "high",
        "low",
        "marketCapitalization",
        "adtvUnits",
        "adtvAmount",
      ];

      keys.forEach((key) => {
        const nseData = latestYear.marketPositioning.find(
          (mp) => mp.exchange.value === "NSE"
        );
        const bseData = latestYear.marketPositioning.find(
          (mp) => mp.exchange.value === "BSE"
        );

        const row: ProductRow = {
          metric: nseData?.[key]?.key || key,
          nse: Number(nseData?.[key]?.value ?? 0),
          bse: Number(bseData?.[key]?.value ?? 0),
        };
        newProductData.push(row);
      });
    } else if (processedData.length > 0) {
      const latestData = processedData[processedData.length - 1];
      const nseValues = processedData
        .map((d) => d.nse)
        .filter((v) => v !== undefined) as number[];
      const bseValues = processedData
        .map((d) => d.bse)
        .filter((v) => v !== undefined) as number[];

      const nseHigh = nseValues.length > 0 ? Math.max(...nseValues) : 0;
      const nseLow = nseValues.length > 0 ? Math.min(...nseValues) : 0;
      const bseHigh = bseValues.length > 0 ? Math.max(...bseValues) : 0;
      const bseLow = bseValues.length > 0 ? Math.min(...bseValues) : 0;

      newProductData.push(
        {
          metric: "Current Price",
          nse: latestData.nse?.toFixed(2) || "N/A",
          bse: latestData.bse?.toFixed(2) || "N/A",
        },
        {
          metric: "Period High",
          nse: nseHigh > 0 ? nseHigh.toFixed(2) : "N/A",
          bse: bseHigh > 0 ? bseHigh.toFixed(2) : "N/A",
        },
        {
          metric: "Period Low",
          nse: nseLow > 0 ? nseLow.toFixed(2) : "N/A",
          bse: bseLow > 0 ? bseLow.toFixed(2) : "N/A",
        },
        {
          metric: "Data Points",
          nse: nseValues.length.toString(),
          bse: bseValues.length.toString(),
        }
      );
    }

    setProductData(newProductData);
  }, [reit, processedData]);

  const getTimeFormat = (domain: [Date, Date]) => {
    const daysDiff =
      (domain[1].getTime() - domain[0].getTime()) / (1000 * 60 * 60 * 24);

    if (daysDiff <= 31) return d3.timeFormat("%d %b"); // 1 month
    if (daysDiff <= 90) return d3.timeFormat("%d %b"); // 3 months
    if (daysDiff <= 365) return d3.timeFormat("%b %Y"); // 1 year
    if (daysDiff <= 365 * 3) return d3.timeFormat("%b %Y"); // 3 years
    return d3.timeFormat("%Y"); // >3 years
  };

  // Chart rendering with zoom (minimap removed to fix infinite loop)
  useEffect(() => {
    if (!chartRef.current || processedData.length === 0) return;

    const svg = d3.select(chartRef.current);

    const renderChart = () => {
      svg.selectAll("*").remove();

      const container = chartRef.current!.parentElement!;
      const width = container.clientWidth;
      const height = 400;
      const margin = { top: 20, right: 80, bottom: 60, left: 60 };

      // Get current data based on active tab with proper filtering
      const currentData = processedData.filter((d) => {
        const value = activeTab === "NSE" ? d.nse : d.bse;
        return value !== undefined && value !== null && !isNaN(value);
      });

      const accessor = (d: ProcessedDataPoint) => {
        const value = activeTab === "NSE" ? d.nse : d.bse;
        return value || 0;
      };
      const color = "#14F2E7";

      // Early return if no valid data
      if (currentData.length === 0) {
        svg
          .append("text")
          .attr("x", width / 2)
          .attr("y", height / 2)
          .attr("text-anchor", "middle")
          .attr("font-family", "Duplet")
          .attr("font-size", "14px")
          .attr("fill", "#666")
          .text(`No ${activeTab} data available`);
        return;
      }

      // Define scales
      const xExtent = d3.extent(currentData, (d) => d.date) as [Date, Date];
      const x = d3
        .scaleTime()
        .domain(xExtent)
        .range([margin.left, width - margin.right]);

      const yValues = currentData
        .map((d) => accessor(d))
        .filter((v) => v !== undefined && !isNaN(v));
      const yExtent = d3.extent(yValues) as [number, number];
      const y = d3
        .scaleLinear()
        .domain(yExtent)
        .nice()
        .range([height - margin.bottom, margin.top]);

      // Create gradients
      const defs = svg.append("defs");

      const gradient = defs
        .append("linearGradient")
        .attr("id", `areaGradient-${activeTab}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", 0)
        .attr("y1", y.range()[0])
        .attr("x2", 0)
        .attr("y2", y.range()[1]);

      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0.1);

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0.3);

      // Create tooltip
      let tooltipDiv = d3
        .select("body")
        .select<HTMLDivElement>(".chart-tooltip");
      if (tooltipDiv.empty()) {
        tooltipDiv = d3
          .select("body")
          .append("div")
          .attr("class", "chart-tooltip");
      }

      tooltipDiv
        .style("position", "absolute")
        .style("background", "rgba(0,0,0,0.9)")
        .style("padding", "12px 16px")
        .style("border-radius", "8px")
        .style("font-size", "13px")
        .style("font-family", "Duplet")
        .style("color", "#fff")
        .style("pointer-events", "none")
        .style("opacity", 0)
        .style("transition", "all 0.2s ease")
        .style("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.4)")
        .style("z-index", "1000")
        .style("border", "1px solid rgba(255,255,255,0.1)");

      // Dynamic time format function
      const getTimeFormat = (domain: [Date, Date], zoomLevel: number) => {
        const diff = domain[1].getTime() - domain[0].getTime();
        const oneDay = 24 * 60 * 60 * 1000;
        const oneMonth = oneDay * 30;
        const oneYear = oneDay * 365;

        // If showing a few days → show day + month
        if (diff < oneMonth) {
          return d3.timeFormat("%d %b"); // e.g. 07 Oct
        }
        // If showing several months → show month + year
        else if (diff < oneYear * 2) {
          return d3.timeFormat("%b %Y"); // e.g. Oct 2025
        }
        // If showing multiple years → just year
        else {
          return d3.timeFormat("%Y");
        }
      };

      // Zoom behavior (simplified, no minimap interaction)
      zoomBehavior.current = d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([1, 50])
        .extent([
          [margin.left, margin.top],
          [width - margin.right, height - margin.bottom],
        ])
        .translateExtent([
          [margin.left, margin.top],
          [width - margin.right, height - margin.bottom],
        ])
        .on("zoom", function (event) {
          const { transform } = event;

          // Validate transform values
          if (
            !isFinite(transform.k) ||
            !isFinite(transform.x) ||
            !isFinite(transform.y) ||
            transform.k <= 0 ||
            transform.k > 50
          ) {
            return;
          }

          // Update scales
          const newX = transform.rescaleX(x);
          const currentDomain = newX.domain();

          // Validate domain
          if (
            !currentDomain[0] ||
            !currentDomain[1] ||
            !isFinite(currentDomain[0].getTime()) ||
            !isFinite(currentDomain[1].getTime())
          ) {
            return;
          }

          // Update axes
          const timeFormat = getTimeFormat(currentDomain, transform.k);

          svg
            .select<SVGGElement>(".x-axis")
            .call(
              d3
                .axisBottom(newX)
                .tickFormat(timeFormat as any)
                .ticks(Math.min(10, Math.max(3, Math.floor(width / 100))))
            )
            .call((g) =>
              g
                .selectAll("text")
                .style("font-size", "12px")
                .style("fill", "#5A607F")
                .style("font-family", "Duplet")
                .attr(
                  "transform",
                  transform.k > 10 ? "rotate(-45)" : "rotate(0)"
                )
                .style("text-anchor", transform.k > 10 ? "end" : "middle")
            );

          // Update grid

          svg
            .select<SVGGElement>(".x-grid")
            .call(
              d3
                .axisBottom(newX)
                .tickSize(-(height - margin.top - margin.bottom))
                .tickFormat(() => "")
                .ticks(Math.min(15, Math.max(5, Math.floor(width / 80))))
            )
            .selectAll("line")
            .attr("stroke", "#E6E9F4")
            .attr("stroke-dasharray", "2,4")
            .attr("opacity", 0.7)
            .attr("shape-rendering", "crispEdges");

          svg.select<SVGGElement>(".x-grid").select(".domain").remove();

          // Update line and area
          const line = d3
            .line<ProcessedDataPoint>()
            .x((d) => {
              const xPos = newX(d.date);
              return isFinite(xPos) ? xPos : 0;
            })
            .y((d) => {
              const yPos = y(accessor(d));
              return isFinite(yPos) ? yPos : 0;
            })
            .defined(
              (d) =>
                accessor(d) !== null &&
                accessor(d) !== undefined &&
                !isNaN(accessor(d))
            )

            .curve(d3.curveMonotoneX);

          const area = d3
            .area<ProcessedDataPoint>()
            .x((d) => {
              const xPos = newX(d.date);
              return isFinite(xPos) ? xPos : 0;
            })
            .y0(y.range()[0])
            .y1((d) => {
              const yPos = y(accessor(d));
              return isFinite(yPos) ? yPos : y.range()[0];
            })
            .defined((d) => {
              const xPos = newX(d.date);
              const yPos = y(accessor(d));
              return isFinite(xPos) && isFinite(yPos);
            })
            .curve(d3.curveMonotoneX);

          // Update paths
          const lineResult = line(currentData);
          const areaResult = area(currentData);

          if (lineResult) {
            svg.select<SVGPathElement>(".line-path").attr("d", lineResult);
          }
          if (areaResult) {
            svg.select<SVGPathElement>(".area-path").attr("d", areaResult);
          }

          svg
            .selectAll<SVGCircleElement, ProcessedDataPoint>(".data-point")
            .attr("cx", (d) => {
              const xPos = newX(d.date);
              return isFinite(xPos) ? xPos : 0;
            })
            .attr("cy", (d) => {
              const yPos = y(accessor(d));
              return isFinite(yPos) ? yPos : 0;
            })
            .style("opacity", 0)
            .style("display", "none");
        });

      // Create main chart group
      const chartGroup = svg.append("g");

      // Add clipping path
      defs
        .append("clipPath")
        .attr("id", `chart-clip-${activeTab}`)
        .append("rect")
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", width - margin.left - margin.right)
        .attr("height", height - margin.top - margin.bottom);

      // Grid
      // Grid
      chartGroup
        .append("g")
        .attr("class", "x-grid")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(
          d3
            .axisBottom(x)
            .tickSize(0) // 🔥 No vertical grid lines
            .tickFormat(() => "")
            .ticks(10)
        );

      chartGroup
        .append("g")
        .attr("class", "y-grid")
        .attr("transform", `translate(${margin.left},0)`)
        .call(
          d3
            .axisLeft(y)
            .tickSize(-(width - margin.left - margin.right))
            .tickFormat(() => "")
            .ticks(8)
        );

      svg
        .selectAll(".x-grid line, .y-grid line")
        .attr("stroke", "#E6E9F4")
        .attr("stroke-dasharray", "2,4")
        .attr("opacity", 0.7);

      svg.selectAll(".x-grid .domain, .y-grid .domain").remove();

      // Area
      const area = d3
        .area<ProcessedDataPoint>()
        .x((d) => x(d.date))
        .y0(y.range()[0])
        .y1((d) => y(accessor(d)))
        .defined((d) => isFinite(x(d.date)) && isFinite(y(accessor(d))))
        .curve(d3.curveMonotoneX);

      chartGroup
        .append("path")
        .datum(currentData)
        .attr("class", "area-path")
        .attr("fill", `url(#areaGradient-${activeTab})`)
        .attr("d", area)
        .attr("clip-path", `url(#chart-clip-${activeTab})`);

      // Line
      const line = d3
        .line<ProcessedDataPoint>()
        .x((d) => x(d.date))
        .y((d) => y(accessor(d)))
        .defined((d) => isFinite(x(d.date)) && isFinite(y(accessor(d))))
        .curve(d3.curveMonotoneX);

      const path = chartGroup
        .append("path")
        .datum(currentData)
        .attr("class", "line-path")
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 2.5)
        .attr("d", line)
        .attr("clip-path", `url(#chart-clip-${activeTab})`);

      // Animate line
      const totalLength = (path.node() as SVGPathElement).getTotalLength();
      path
        .attr("stroke-dasharray", `${totalLength} ${totalLength}`)
        .attr("stroke-dashoffset", totalLength)
        .transition()
        .duration(2000)
        .ease(d3.easeCubic)
        .attr("stroke-dashoffset", 0);

      // Points (hidden initially, shown on zoom)
      chartGroup
        .selectAll(".data-point")
        .data(currentData)
        .enter()
        .append("circle")
        .attr("class", "data-point")
        .attr("cx", (d) => x(d.date))
        .attr("cy", (d) => y(accessor(d)))
        .attr("r", 3)
        .attr("fill", color)
        .attr("stroke", "#fff")
        .attr("stroke-width", 2)
        .style("opacity", 0)
        .attr("clip-path", `url(#chart-clip-${activeTab})`);

      // Crosshair
      const crosshair = chartGroup
        .append("g")
        .attr("class", "crosshair")
        .style("opacity", 0);

      crosshair
        .append("line")
        .attr("class", "crosshair-x")
        .attr("y1", margin.top)
        .attr("y2", height - margin.bottom)
        .attr("stroke", "#666")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3");

      // Hover overlay
      const hoverRect = chartGroup
        .append("rect")
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", width - margin.left - margin.right)
        .attr("height", height - margin.top - margin.bottom)
        .attr("fill", "none")
        .attr("pointer-events", "all");

      hoverRect
        .on("mousemove", function (event) {
          const [mouseX] = d3.pointer(event);
          const svgNode = svg.node();
          if (!svgNode) return;

          try {
            const transform = d3.zoomTransform(svgNode);
            const currentX = transform.rescaleX(x);
            const date = currentX.invert(mouseX);

            if (!date || !isFinite(date.getTime())) return;

            // Find closest data point
            const bisectDate = d3.bisector(
              (d: ProcessedDataPoint) => d.date
            ).left;
            const i = bisectDate(currentData, date, 1);
            const d0 = currentData[i - 1];
            const d1 = currentData[i];

            if (!d0 && !d1) return;

            const d =
              d1 &&
              d0 &&
              date.getTime() - d0.date.getTime() >
                d1.date.getTime() - date.getTime()
                ? d1
                : d0 || d1;

            if (d && isFinite(accessor(d))) {
              const xPos = currentX(d.date);
              const yPos = y(accessor(d));

              if (isFinite(xPos) && isFinite(yPos)) {
                crosshair
                  .style("opacity", 1)
                  .select(".crosshair-x")
                  .attr("x1", xPos)
                  .attr("x2", xPos);

                tooltipDiv
                  .style("opacity", 1)
                  .html(
                    `
                    <div style="font-weight: bold; margin-bottom: 4px;">${activeTab}</div>
                    <div>Date: ${d.date.toLocaleDateString()}</div>
                    <div>Price: ₹${safeFormat(accessor(d))}</div>
                  `
                  )
                  .style("left", event.pageX + 15 + "px")
                  .style("top", event.pageY - 10 + "px");
              }
            }
          } catch (error) {
            console.warn("Mousemove error:", error);
          }
        })
        .on("mouseleave", function () {
          crosshair.style("opacity", 0);
          tooltipDiv.style("opacity", 0);
        });

      // Axes
      const initialFormat = getTimeFormat(x.domain() as [Date, Date], 1);

      chartGroup
        .append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(
          d3
            .axisBottom(x)
            .tickFormat(initialFormat as any)
            .ticks(Math.min(10, Math.max(3, Math.floor(width / 100))))
        )
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("line").remove())
        .call((g) =>
          g
            .selectAll("text")
            .style("font-size", "12px")
            .style("fill", "#5A607F")
            .style("font-family", "Duplet")
        );

      chartGroup
        .append("g")
        .attr("class", "y-axis")
        .attr("transform", `translate(${margin.left},0)`)
        .call(d3.axisLeft(y).ticks(8))
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("line").remove())
        .call((g) =>
          g
            .selectAll("text")
            .style("font-size", "12px")
            .style("fill", "#5A607F")
            .style("font-family", "Duplet")
        );

      // Add axis labels
      svg
        .append("text")
        .attr("class", "x-axis-label")
        .attr("x", width / 2)
        .attr("y", height)
        .attr("text-anchor", "middle")
        .attr("font-family", "Duplet")
        .attr("font-size", "15px")
        .attr("font-weight", "bold")
        .attr("fill", "#132c49")
        .text("Date");

      svg
        .append("text")
        .attr("class", "y-axis-label")
        .attr("transform", "rotate(-90)")
        .attr("x", -(height / 2) + 20)
        .attr("y", 10)
        .attr("text-anchor", "middle")
        .attr("font-family", "Duplet")
        .attr("font-size", "15px")
        .attr("font-weight", "bold")
        .attr("fill", "#132c49")
        .text("Price (₹)");

      if (zoomBehavior.current) {
        svg.call(zoomBehavior.current).on("dblclick.zoom", null);
      }

      return () => {
        d3.select("body").select(".chart-tooltip").remove();
      };
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            renderChart();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(chartRef.current);

    return () => {
      observer.disconnect();
    };
  }, [processedData, activeTab]);

  const resetZoom = () => {
    if (chartRef.current && zoomBehavior.current) {
      const svg = d3.select(chartRef.current);
      svg
        .transition()
        .duration(750)
        .call(zoomBehavior.current.transform, d3.zoomIdentity);
    }
  };

  const zoomToRange = (days: number) => {
    if (
      !chartRef.current ||
      processedData.length === 0 ||
      !zoomBehavior.current
    )
      return;

    const svg = d3.select(chartRef.current);
    const width = chartRef.current.parentElement?.clientWidth || 800;
    const margin = { top: 20, right: 80, bottom: 60, left: 60 };

    const xExtent = d3.extent(processedData, (d) => d.date) as [Date, Date];
    const latestDate = xExtent[1];
    const startDate = new Date(latestDate);
    startDate.setDate(latestDate.getDate() - days);
    const xRange: [Date, Date] = [startDate, latestDate];

    // Compute scale and translate for zoom
    const scale =
      (xExtent[1].getTime() - xExtent[0].getTime()) /
      (xRange[1].getTime() - xRange[0].getTime());
    const translate = -(
      ((xRange[0].getTime() - xExtent[0].getTime()) /
        (xExtent[1].getTime() - xExtent[0].getTime())) *
      (width - margin.left - margin.right)
    );
    const t = d3.zoomIdentity.scale(scale).translate(translate, 0);

    // Apply zoom transform
    svg.transition().duration(750).call(zoomBehavior.current.transform, t);

    // Update x-axis using the transformed scale
    const xOriginal = d3
      .scaleTime()
      .domain(xExtent)
      .range([margin.left, width - margin.right]);
    const xTransformed = t.rescaleX(xOriginal);

    const xDomain = xTransformed.domain();
    if (xDomain.length !== 2) return; // ⚡ safety check for TS

    const xAxis = d3
      .axisBottom(xTransformed)
      .ticks(Math.min(10, Math.max(3, Math.floor(width / 100))))
      .tickFormat(getTimeFormat(xDomain as [Date, Date]) as any);

    svg.select<SVGGElement>(".x-axis").transition().duration(750).call(xAxis);
  };

  if (!reit) {
    return (
      <section className="market">
        <div className="market__card">
          <div className="market__header">
            <h2>Market Performance</h2>
            <p>No REIT data available</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="market">
      <div className="market__card">
        <div className="market__header">
          <h2>Market Performance</h2>
          {/* <p className="data-count">
            {processedData.length} data points across{" "}
            {Math.round(
              ((processedData[processedData.length - 1]?.date.getTime() -
                processedData[0]?.date.getTime()) /
                (1000 * 60 * 60 * 24 * 365)) *
                10
            ) / 10}{" "}
            years
          </p> */}

          <div className="market__subheader">
            <div className="market__tabs">
              <button
                className={activeTab === "NSE" ? "active" : ""}
                onClick={() => setActiveTab("NSE")}
              >
                NSE <span>National Stock Exchange</span>
              </button>
              <button
                className={activeTab === "BSE" ? "active" : ""}
                onClick={() => setActiveTab("BSE")}
              >
                BSE <span>Bombay Stock Exchange</span>
              </button>
            </div>

            <div className="market__controls">
              <div className="zoom-options">
                <button
                  className={activeRange === 30 ? "active" : ""}
                  onClick={() => {
                    zoomToRange(30);
                    setActiveRange(30);
                  }}
                >
                  1M
                </button>
                <button
                  className={activeRange === 90 ? "active" : ""}
                  onClick={() => {
                    zoomToRange(90);
                    setActiveRange(90);
                  }}
                >
                  3M
                </button>
                <button
                  className={activeRange === 365 ? "active" : ""}
                  onClick={() => {
                    zoomToRange(365);
                    setActiveRange(365);
                  }}
                >
                  1Y
                </button>
                <button
                  className={activeRange === 365 * 3 ? "active" : ""}
                  onClick={() => {
                    zoomToRange(365 * 3);
                    setActiveRange(365 * 3);
                  }}
                >
                  3Y
                </button>
                <button
                  className={activeRange === 365 * 5 ? "active" : ""}
                  onClick={() => {
                    zoomToRange(365 * 5);
                    setActiveRange(365 * 5);
                  }}
                >
                  5Y
                </button>
              </div>

              <button
                className="zoom-reset-btn"
                onClick={() => {
                  resetZoom();
                  setActiveRange(null);
                }}
              >
                Reset Zoom
              </button>
            </div>
          </div>
        </div>

        {/* Main Chart */}
        {processedData.length > 0 ? (
          <div style={{ position: "relative" }}>
            <svg
              ref={chartRef}
              width="100%"
              height={400}
              style={{ display: "block" }}
            ></svg>
          </div>
        ) : (
          <div
            style={{
              height: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p>No historical data available for {reit.name}</p>
          </div>
        )}

        {/* Table */}
        {productData.length > 0 && (
          <div className="market__table">
            <h3>Market Data</h3>
            <table>
              <thead>
                <tr>
                  <th>Exchange</th>
                  <th>NSE</th>
                  <th>BSE</th>
                </tr>
              </thead>
              <tbody>
                {productData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.metric}</td>
                    <td>
                      {row.metric.toLowerCase().includes("market cap")
                        ? toWholeNumber(row.nse)
                        : safeFormat(row.nse)}
                    </td>
                    <td>
                      {row.metric.toLowerCase().includes("market cap")
                        ? toWholeNumber(row.bse)
                        : safeFormat(row.bse)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default Market;
